import { useEffect } from "react";
import {
  ArrowLeft,
  ArrowUp,
  CheckCircle2,
  CircleDot,
  Download,
  ShieldCheck,
} from "lucide-react";
import { ContactButton } from "../components/ContactButton";
import { FadeIn } from "../components/FadeIn";
import { profile, resumeDownloadName, resumeHref, type Project } from "../content/portfolio";

export function ProjectDetailPage({ project }: { project: Project }) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${project.shortTitle}｜${profile.name}`;
    window.scrollTo(0, 0);
    return () => {
      document.title = previousTitle;
    };
  }, [project.shortTitle]);

  return (
    <div className="project-detail-page">
      <a className="skip-link" href="#project-detail-main">
        跳到主要内容
      </a>

      <header className="project-detail-nav">
        <a href="/#projects" className="project-detail-back">
          <ArrowLeft aria-hidden="true" />
          返回项目
        </a>
        <a href="/" className="project-detail-brand" aria-label="返回陈沐彬个人网站首页">
          <strong>CM</strong>
          <span>QA / DEV</span>
        </a>
      </header>

      <main id="project-detail-main" tabIndex={-1}>
        <section className="project-detail-hero" aria-labelledby="project-detail-heading">
          <FadeIn className="project-detail-number" y={20} aria-hidden="true">
            {project.number}
          </FadeIn>
          <div className="project-detail-title-group">
            <FadeIn as="p" y={18} className="project-detail-meta">
              <span>{project.ownership}</span>
              <time>{project.period}</time>
            </FadeIn>
            <FadeIn as="h1" id="project-detail-heading" delay={0.06} y={28}>
              {project.title}
            </FadeIn>
            <FadeIn as="p" delay={0.12} y={18} className="project-detail-summary">
              {project.summary}
            </FadeIn>
          </div>
        </section>

        <section className="project-detail-flow" aria-labelledby="project-flow-heading">
          <div>
            <h2 id="project-flow-heading">执行链路</h2>
            <p>从输入到结论，关键步骤都保留可核对的上下文。</p>
          </div>
          <ol>
            {project.flow.map((step, index) => (
              <li key={step}>
                <CircleDot aria-hidden="true" />
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{step}</strong>
              </li>
            ))}
          </ol>
        </section>

        <div className="project-detail-content">
          <section className="project-detail-record" aria-labelledby="project-context-heading">
            <h2 id="project-context-heading">项目上下文</h2>
            <dl>
              <div>
                <dt>背景</dt>
                <dd>{project.background}</dd>
              </div>
              <div>
                <dt>职责</dt>
                <dd>{project.responsibility}</dd>
              </div>
              <div>
                <dt>难点</dt>
                <dd>{project.challenges}</dd>
              </div>
              <div>
                <dt>价值</dt>
                <dd>{project.value}</dd>
              </div>
            </dl>
          </section>

          <section className="project-detail-record" aria-labelledby="project-approach-heading">
            <h2 id="project-approach-heading">方案与实现</h2>
            <ul className="project-detail-approach">
              {project.approach.map((item) => (
                <li key={item}>
                  <CheckCircle2 aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="project-detail-evidence" aria-labelledby="project-evidence-heading">
            <div className="project-detail-evidence-copy">
              <h2 id="project-evidence-heading">可核对证据</h2>
              <p>仅呈现简历中明确记录的验证范围与基线，不推导额外成果。</p>
            </div>
            <ul>
              {project.metrics.map((metric) => <li key={metric}>{metric}</li>)}
            </ul>
          </section>

          <section className="project-detail-record project-detail-boundary" aria-labelledby="project-boundary-heading">
            <div>
              <ShieldCheck aria-hidden="true" />
              <h2 id="project-boundary-heading">事实边界</h2>
            </div>
            <p>{project.boundary}</p>
          </section>

          <section className="project-detail-stack" aria-labelledby="project-stack-heading">
            <h2 id="project-stack-heading">技术栈</h2>
            <ul>
              {project.stack.map((tool) => <li key={tool}>{tool}</li>)}
            </ul>
          </section>
        </div>

        <section className="project-detail-cta" aria-label="项目详情操作">
          <div>
            <h2>继续核对</h2>
            <p>返回项目列表查看其他案例，或下载完整简历。</p>
          </div>
          <div>
            <a className="resume-button" href="/#projects">
              <ArrowLeft aria-hidden="true" />
              返回项目
            </a>
            <ContactButton
              href={resumeHref}
              download={resumeDownloadName}
              icon={<Download className="h-4 w-4" aria-hidden="true" />}
            >
              下载简历
            </ContactButton>
          </div>
        </section>
      </main>

      <footer className="project-detail-footer">
        <span>{profile.name} / {profile.role}</span>
        <a href="#project-detail-main" aria-label="返回项目详情顶部">
          <ArrowUp aria-hidden="true" />
        </a>
      </footer>
    </div>
  );
}
